/**
 * Accept.js client helper (Track B Phase 1b — SANDBOX ONLY).
 *
 * Card data goes straight from the browser to Authorize.net and comes back as a
 * one-time payment nonce (opaqueData). The PAN never touches our servers. We
 * load the SANDBOX Accept.js (jstest.authorize.net); production is out of scope
 * for this phase.
 */

// Accept.js attaches a global `Accept` object; it has no official types.
declare global {
  interface Window {
    Accept?: any;
  }
}

// NOTE: the Accept.js <script> tag now lives statically in public/index.html
// (sandbox URL). No URL constant is needed here anymore.

export interface AcceptConfig {
  apiLoginId: string;
  clientKey: string;
}

export interface CardInput {
  cardNumber: string; // digits only
  month: string; // MM
  year: string; // YYYY
  cardCode: string; // CVV
}

export interface PaymentNonce {
  dataDescriptor: string;
  dataValue: string;
}

let loadPromise: Promise<void> | null = null;

/** True once Accept.js has finished initializing its dispatchData API. */
const acceptReady = (): boolean =>
  typeof window.Accept?.dispatchData === 'function';

/**
 * Poll for `window.Accept.dispatchData` to become callable. Accept.js attaches
 * `window.Accept` ASYNCHRONOUSLY after the script's `onload` fires, so trusting
 * onload alone races: dispatchData can be undefined when we call it, yielding
 * "Accept.js is not loaded correctly" and a downstream 400 (nonce never made).
 * Resolves once it's ready; rejects after `timeoutMs`.
 */
const waitForAccept = (timeoutMs = 10000, intervalMs = 100): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    if (acceptReady()) {
      resolve();
      return;
    }
    const start = Date.now();
    const timer = setInterval(() => {
      if (acceptReady()) {
        clearInterval(timer);
        resolve();
      } else if (Date.now() - start >= timeoutMs) {
        clearInterval(timer);
        reject(new Error('Accept.js did not initialize in time. Please retry.'));
      }
    }, intervalMs);
  });

// Accept.js script URL is chosen from REACT_APP_ACCEPTJS_ENV, which CRA INLINES
// into the JS bundle at build time (webpack DefinePlugin). That guarantees the
// value bakes into app code — and the bundle hash changes between sandbox and
// production builds — unlike an index.html %PLACEHOLDER% which does not affect
// the bundle. Default (unset / anything != 'production') = sandbox (fail-safe).
const ACCEPT_JS_URL =
  process.env.REACT_APP_ACCEPTJS_ENV === 'production'
    ? 'https://js.authorize.net/v1/Accept.js'
    : 'https://jstest.authorize.net/v1/Accept.js';

/**
 * Inject the correct Accept.js for the build env, then WAIT until its dispatchData
 * API is actually callable before resolving. `onload` is NOT trusted — Accept.js
 * attaches dispatchData asynchronously after its internal AcceptCore.js loads, so
 * the readiness poll (waitForAccept) is what preserves the load-race fix.
 */
export const loadAcceptJs = (): Promise<void> => {
  if (acceptReady()) return Promise.resolve();
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<void>((resolve, reject) => {
    // Inject once — guard against duplicates across retries / multiple callers.
    if (!document.querySelector('script[data-acceptjs="1"]')) {
      const script = document.createElement('script');
      script.src = ACCEPT_JS_URL;
      script.async = true;
      script.setAttribute('data-acceptjs', '1');
      script.onerror = () => {
        loadPromise = null;
        reject(new Error('Failed to load Accept.js.'));
      };
      document.head.appendChild(script);
    }
    // Resolve on dispatchData readiness, NOT on onload (the race fix).
    waitForAccept().then(resolve).catch((err) => {
      loadPromise = null;
      reject(err);
    });
  });
  return loadPromise;
};

/**
 * Exchange raw card fields for a one-time payment nonce via Accept.js.
 * Rejects with a readable message if Authorize.net declines the card fields.
 */
export const getPaymentNonce = async (
  config: AcceptConfig,
  card: CardInput,
): Promise<PaymentNonce> => {
  await loadAcceptJs();
  if (!window.Accept) throw new Error('Accept.js is not available.');

  const secureData = {
    authData: {
      clientKey: config.clientKey,
      apiLoginID: config.apiLoginId,
    },
    cardData: {
      cardNumber: card.cardNumber.replace(/\s+/g, ''),
      month: card.month,
      year: card.year,
      cardCode: card.cardCode,
    },
  };

  // One dispatchData call. Resolves with the nonce on success; resolves { retry:true }
  // when Accept.js itself reports it isn't fully initialized yet (its outer script sets
  // dispatchData before AcceptCore.js finishes, so the FIRST call can come back with
  // code E_WC_05 / "Accept.js is not loaded correctly" — the "fails first, works second"
  // symptom). Any other failure rejects. NOTE: we only ever read response.messages here —
  // never response.opaqueData or any card field is logged.
  const dispatchOnce = (): Promise<PaymentNonce | { retry: true; msg: string }> =>
    new Promise((resolve, reject) => {
      window.Accept.dispatchData(secureData, (response: any) => {
        if (response?.messages?.resultCode === 'Ok') {
          resolve({
            dataDescriptor: response.opaqueData.dataDescriptor,
            dataValue: response.opaqueData.dataValue,
          });
        } else {
          const first = response?.messages?.message?.[0] || {};
          const code = String(first.code || '');
          const text = String(first.text || 'Card could not be validated.');
          const notReady = code === 'E_WC_05' || /not loaded correctly/i.test(text);
          if (notReady) resolve({ retry: true, msg: text });
          else reject(new Error(text));
        }
      });
    });

  // Retry the not-ready case transparently so the user never has to click twice.
  const MAX_ATTEMPTS = 4;
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    if (attempt > 0) await new Promise((r) => setTimeout(r, 300));
    const out = await dispatchOnce();
    if (!('retry' in out)) return out; // got the nonce
  }
  throw new Error('Card verification is still initializing. Please try again in a moment.');
};
